import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { ftthCaixaService, FtthCaixa } from '../../services/ftthCaixaService';
import { caboService, Cabo } from '../../services/caboService';
import { MapSelectorModal } from '../common';
import EscritorioSelect from '../selects/EscritorioSelect';

export interface FtthCaixaReservaFormProps {
  onSuccess?: (caixa: FtthCaixa) => void;
  onCancel?: () => void;
  caixa?: FtthCaixa;
  coordinates?: { lat: number; lng: number };
  isFullscreen?: boolean;
}

interface FtthCaixaReservaFormData {
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  cabo_id: number | '';
  metragem: string;
  escritorio_id: number;
}

const FtthCaixaReservaForm: React.FC<FtthCaixaReservaFormProps> = ({
  onSuccess,
  onCancel,
  caixa,
  coordinates,
  isFullscreen,
}) => {
  const [formData, setFormData] = useState<FtthCaixaReservaFormData>({
    name: caixa?.name || '',
    address: caixa?.address || '',
    latitude: coordinates?.lat ? coordinates.lat.toString() : caixa?.latitude ? caixa.latitude.toString() : '',
    longitude: coordinates?.lng ? coordinates.lng.toString() : caixa?.longitude ? caixa.longitude.toString() : '',
    cabo_id: (caixa as any)?.cabo_id ?? '',
    metragem: (caixa as any)?.metragem != null ? String((caixa as any).metragem) : '',
    escritorio_id: (caixa as any)?.escritorio_id || 1,
  });

  const [cabos, setCabos] = useState<Cabo[]>([]);
  const [loadingCabos, setLoadingCabos] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const loadCabos = async () => {
      setLoadingCabos(true);
      try {
        const response = await caboService.getCabos({ limit: 500 });
        setCabos(response.cabos || []);
      } catch {
        setCabos([]);
      } finally {
        setLoadingCabos(false);
      }
    };
    loadCabos();
  }, []);

  useEffect(() => {
    if (coordinates) {
      setFormData(prev => ({
        ...prev,
        latitude: coordinates.lat.toString(),
        longitude: coordinates.lng.toString(),
      }));
    }
  }, [coordinates]);

  const handleInputChange = (field: keyof FtthCaixaReservaFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleCaboChange = (e: SelectChangeEvent<string | number>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      cabo_id: value === '' ? '' : Number(value),
    }));
    setError(null);
  };

  const handleEscritorioChange = (escritorioId: number | number[] | null) => {
    const id = Array.isArray(escritorioId) ? escritorioId[0] : escritorioId;
    if (id) setFormData(prev => ({ ...prev, escritorio_id: id }));
  };

  const handleOpenMapModal = () => {
    const lat = formData.latitude ? parseFloat(formData.latitude) : undefined;
    const lng = formData.longitude ? parseFloat(formData.longitude) : undefined;
    if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
      setSelectedLocation({ lat, lng });
    }
    setMapModalOpen(true);
  };

  const handleCloseMapModal = () => setMapModalOpen(false);

  const handleConfirmMapLocation = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString(),
    }));
    setSelectedLocation({ lat, lng });
    setMapModalOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    if (!formData.address.trim()) {
      setError('Endereço é obrigatório');
      return;
    }
    if (!formData.escritorio_id) {
      setError('Escritório é obrigatório');
      return;
    }

    setLoading(true);
    try {
      const payload: Partial<FtthCaixa> = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        tipo: 'reserva',
        status: 'ativo',
        escritorio_id: formData.escritorio_id,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        cabo_id: formData.cabo_id ? Number(formData.cabo_id) : undefined,
        metragem: formData.metragem ? parseFloat(formData.metragem) : undefined,
        capacidade: null,
        disabled_ports: [],
      };

      let result;
      if (caixa?.id) {
        result = await ftthCaixaService.update(caixa.id, payload);
      } else {
        result = await ftthCaixaService.create(payload);
      }

      const isSuccess =
        result &&
        (result.success === true || result.data || ('id' in result && (result as any).id));

      if (isSuccess) {
        const caixaData = (result as any).data || result;
        onSuccess?.(caixaData as FtthCaixa);
      } else {
        setError('Erro ao salvar reserva');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Erro ao salvar reserva');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>
        {caixa ? 'Editar Reserva' : 'Nova Reserva'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Nome"
            required
            value={formData.name}
            onChange={handleInputChange('name')}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Endereço"
            required
            value={formData.address}
            onChange={handleInputChange('address')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Cabo</InputLabel>
            <Select
              value={formData.cabo_id}
              onChange={handleCaboChange}
              label="Cabo"
              disabled={loadingCabos}
            >
              <MenuItem value="">
                <em>Nenhum (vincular no mapa)</em>
              </MenuItem>
              {cabos.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.titulo || `Cabo #${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Metragem (m)"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={formData.metragem}
            onChange={handleInputChange('metragem')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <EscritorioSelect
            value={formData.escritorio_id}
            onChange={handleEscritorioChange}
            required
            disablePortal={isFullscreen}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Latitude"
            value={formData.latitude}
            onChange={handleInputChange('latitude')}
            InputProps={{
              endAdornment: (
                <LocationIcon
                  sx={{ cursor: 'pointer', color: 'primary.main' }}
                  onClick={handleOpenMapModal}
                />
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Longitude"
            value={formData.longitude}
            onChange={handleInputChange('longitude')}
          />
        </Grid>
      </Grid>

      <Box display="flex" gap={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Salvando...' : (caixa ? 'Atualizar' : 'Criar Reserva')}
        </Button>
      </Box>

      <MapSelectorModal
        open={mapModalOpen}
        onClose={handleCloseMapModal}
        onConfirm={handleConfirmMapLocation}
        initialCoordinates={selectedLocation}
        title="Selecionar Localização no Mapa"
        description="Clique no mapa para selecionar a localização da reserva"
      />
    </Box>
  );
};

export default FtthCaixaReservaForm;
